/**
 * 1.8.0 genealogy: what an asset is made from (docs/dev/rights-1.8.0-build.md, plan section 1). Step 0's file.
 *
 * When a sequence is saved as an asset (an export saved as an asset, or a check-in's rendered master), PRISM records
 * one asset_sources row (0106) per use of a source, for good: nobody can change or delete a row (the table's
 * trigger), except the SET NULL of a purged source's ids and the purge of the made asset itself.
 *
 * sourcesOf is the one builder of those rows from a sequence document. One row per use, in track order (V1, V2…,
 * A2…A8, GFX 1–3), each track by record time:
 *  - V1 clips at their storyline place (storyline.ts toTimeline). A clip cut from a multicam group carries the angle
 *    it shows (its own versionId), so each segment records the angle actually used; the group's other angles are not
 *    uses;
 *  - the video tracks' clips (V2 is layers[0]);
 *  - A2 (`audio`) and A3–A8 (`audioTracks`);
 *  - graphics: the template's asset when the template is an asset, otherwise the built-in template's id and version.
 * Every clip on the timeline counts, muted or on a hidden track, as preflight and the export lineage count them.
 * A source that is a still image is kind `still`; a source with sound only is kind `audio` (on V1 its track is A1).
 */
import { allAudioTracks, clipDuration, graphicTrackOf, type SequenceDocument } from './sequence.ts';
import { toTimeline } from './storyline.ts';
import type { RightsSummary } from './rights.ts';

export const GENEALOGY_KINDS = ['footage', 'audio', 'still', 'graphic'] as const;
export type GenealogyKind = (typeof GENEALOGY_KINDS)[number];

/** asset_sources.origin: how the row was written. */
export const GENEALOGY_ORIGINS = ['export', 'checkin', 'backfill'] as const;
export type GenealogyOrigin = (typeof GENEALOGY_ORIGINS)[number];

/** What sourcesOf needs to know about the document's sources (looked up by the caller). */
export interface GenealogySourceInfo {
  /** By version id: its asset, and whether it is a still image, sound only or a picture. Unknown versions count as footage of the clip's asset. */
  versions?: Readonly<Record<string, { assetId: string; kind: 'video' | 'audio' | 'still' }>>;
  /** By graphic template id: the template's asset (and version) when the template is an asset; absent or null for a built-in template. */
  templates?: Readonly<Record<string, { assetId: string; versionId?: string | null } | null>>;
}

/** One use of a source in a document. Ticks: source range in the source, record range in the sequence. */
export interface GenealogyUse {
  kind: GenealogyKind;
  /** V1, V2…V9, A1, A2…A8, GFX 1–3. */
  track: string;
  assetId?: string;
  versionId?: string;
  /** A graphic's template (always set for graphics; the only reference for a built-in template). */
  templateId?: string;
  templateVersion?: number;
  sourceInTicks: number;
  sourceOutTicks: number;
  recordInTicks: number;
  recordOutTicks: number;
}

/** The uses of every source in a sequence document (the module comment says what counts). */
export function sourcesOf(doc: SequenceDocument, sourceInfo: GenealogySourceInfo = {}): GenealogyUse[] {
  const out: GenealogyUse[] = [];
  const kindOf = (versionId: string, picture: boolean): GenealogyKind => {
    const k = sourceInfo.versions?.[versionId]?.kind;
    return k === 'still' ? 'still' : k === 'audio' || !picture ? 'audio' : 'footage';
  };
  const assetOf = (c: { assetId: string; versionId: string }) => sourceInfo.versions?.[c.versionId]?.assetId ?? c.assetId;
  for (const it of toTimeline(doc)) {
    const kind = kindOf(it.clip.versionId, true);
    out.push({
      kind, track: kind === 'audio' ? 'A1' : 'V1', assetId: assetOf(it.clip), versionId: it.clip.versionId,
      sourceInTicks: it.clip.sourceInTicks, sourceOutTicks: it.clip.sourceOutTicks, recordInTicks: it.start, recordOutTicks: it.end,
    });
  }
  (doc.layers ?? []).forEach((layer, i) => {
    for (const c of [...layer.clips].sort((a, b) => a.recordInTicks - b.recordInTicks)) {
      out.push({
        kind: kindOf(c.versionId, true), track: `V${i + 2}`, assetId: assetOf(c), versionId: c.versionId,
        sourceInTicks: c.sourceInTicks, sourceOutTicks: c.sourceOutTicks, recordInTicks: c.recordInTicks, recordOutTicks: c.recordInTicks + clipDuration(c),
      });
    }
  });
  for (const t of allAudioTracks(doc)) {
    for (const c of [...t.clips].sort((a, b) => a.recordInTicks - b.recordInTicks)) {
      out.push({
        kind: 'audio', track: `A${t.number}`, assetId: assetOf(c), versionId: c.versionId,
        sourceInTicks: c.sourceInTicks, sourceOutTicks: c.sourceOutTicks, recordInTicks: c.recordInTicks, recordOutTicks: c.recordInTicks + clipDuration(c),
      });
    }
  }
  const graphics = doc.graphics.map((g, i) => ({ g, i })).sort((a, b) => graphicTrackOf(a.g) - graphicTrackOf(b.g) || a.g.recordInTicks - b.g.recordInTicks || a.i - b.i);
  for (const { g } of graphics) {
    const tpl = sourceInfo.templates?.[g.templateId] ?? null;
    out.push({
      kind: 'graphic', track: `GFX ${graphicTrackOf(g) + 1}`,
      ...(tpl ? { assetId: tpl.assetId, ...(tpl.versionId ? { versionId: tpl.versionId } : {}) } : {}),
      templateId: g.templateId, templateVersion: g.templateVersion,
      sourceInTicks: 0, sourceOutTicks: g.durationTicks, recordInTicks: g.recordInTicks, recordOutTicks: g.recordInTicks + g.durationTicks,
    });
  }
  return out;
}

/** The asset ids a document uses (clips and graphic assets), once each, in the order of sourcesOf. */
export function sourceAssetIds(uses: readonly GenealogyUse[]): string[] {
  return [...new Set(uses.flatMap((u) => (u.assetId ? [u.assetId] : [])))];
}

// ---------------------------------------------------------------- the API (GET /api/v1/assets/:id/genealogy)

/** The rights recorded with a use (asset_sources.rights_snapshot): the source's effective rights at that moment. */
export type RightsSnapshot = RightsSummary;

/** One recorded use of a source in the made asset (an asset_sources row as the reader may see it). */
export interface AssetSourceDTO {
  id: string;
  kind: GenealogyKind;
  track: string;
  /** The source, if it still exists (null once purged: `purged`); title and code are the snapshot taken when recorded. */
  source: { assetId: string | null; versionId: string | null; code: string | null; title: string | null; versionNumber: number | null; purged: boolean };
  graphicTemplate: { id: string; version: number | null } | null;
  sourceInTicks: number | null;
  sourceOutTicks: number | null;
  recordInTicks: number | null;
  recordOutTicks: number | null;
  /** The sequence and version it was made from; ids null once deleted (the name is the snapshot). */
  sequence: { id: string | null; versionId: string | null; name: string | null };
  origin: GenealogyOrigin;
  exportId: string | null;
  checkinId: string | null;
  /** While rights management is on: the rights recorded then, and the source's rights now. */
  rightsThen?: RightsSnapshot | null;
  rightsNow?: RightsSummary | null;
  createdAt: string;
}

/** An asset made with this one (Used in). */
export interface GenealogyUsedInDTO {
  assetId: string;
  code: string | null;
  title: string;
  thumbnailUrl: string | null;
  /** How many uses of this asset the made asset records. */
  uses: number;
  createdAt: string;
  rights?: RightsSummary | null;
}

export interface GenealogyDTO {
  /** The uses this asset is made from, in the order recorded (sourcesOf's). */
  madeFrom: AssetSourceDTO[];
  /** The assets made with this one, newest first, paged by nextCursor. */
  usedIn: GenealogyUsedInDTO[];
  /** Sources and made assets the reader can't see, counted ("1 restricted source") instead of shown. */
  hiddenCount: number;
  nextCursor: string | null;
}

/**
 * Stream A's addition (1.8.0, additive): what GET /api/v1/assets/:id/genealogy answers, the GenealogyDTO and
 * usedInCount: how many assets the reader may see (not in the recycle bin) are made with this one (the recycle and
 * purge dialogs' "Used in N assets; the record of that use is kept").
 */
export interface GenealogyPageDTO extends GenealogyDTO {
  usedInCount: number;
}
